import React, {useState} from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import { axiosDelay } from "../../config/config";

import {
	appSetAlert, appSetModalContent
} from "../../redux/app/appActions";

import {
    dataReloadDependants
} from "../../redux/data/dataActions";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Button from '@mui/material/Button';
import Input from "@mui/material/Input";
import StarIcon from "@mui/icons-material/Star";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import LinearProgress from "@mui/material/LinearProgress";
import {useTranslation} from "react-i18next";
import useDataSource from "../../hooks/useDataSource";


const PREFIX = 'ProjectHubSetupClient';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    form: `${PREFIX}-form`,
    margin: `${PREFIX}-margin`,
    formEmbeddedActions: `${PREFIX}-formEmbeddedActions`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
		width: '100%',
		height: '100%',
		//overflow: 'hidden'
	},

    [`& .${classes.linearProgress}`]: {
		height: '1px'
	},

    [`& .${classes.card}`]: {
		height: '100%',
		width: '100%'
	},

    [`& .${classes.form}`]: {
		height: '350px'
	},

    [`& .${classes.margin}`]: {
		margin: theme.spacing(1),
		width: 'calc(100% - 16px)'
	},

    [`& .${classes.formEmbeddedActions}`]: {
		// position: 'absolute',
		// bottom: '0px',
		width: 'calc(100% - 32px)'
	}
}));

export default function ProjectHubSetupClient({configuration, xId}) {

	const dispatch = useDispatch();
	const { t } = useTranslation();

	// App State
	const config = useSelector(state => state.config);
	const isThemeLight = useSelector(state => state.persist.themeLight);

	// Component State
	const dataSourceName = configuration?.dataSource?.name ? configuration?.dataSource?.name : '';

	const dataSource = useDataSource(dataSourceName);
	const [showProgress, setShowProgress] = useState(true);


	const clientIsUnique = async (name) => {
		const clientExists = dataSource.data.find(d => d.clientname.toLowerCase() === name.toLowerCase());
		if (clientExists) {
			return false;
		} else {
			return true;
		}
	};

	const schema = yup.object().shape({
		name: yup.string().required().test("Client must be unique", "Client must be unique", clientIsUnique),
		description: yup.string().required()
	});

    const resolver = yupResolver(schema);
    const { register, handleSubmit, formState, formState: { errors } } = useForm({ resolver });

    const onSubmit = dataToPost => {

        // TODO: Any additional validation
		const clientExists = dataSource.data.find(d => d.clientname.toLowerCase() === dataToPost.name.toLowerCase());
		if (clientExists) {
			// Close the dialog and signal a reload
			handleClose();
			return;
		}

		createClient(dataToPost);
    };

	const handleClose = () => {
		dispatch(appSetModalContent(null));
	}

    const createClient = dataToPost => {

        // Before we post get the user's ObjectID
        setTimeout(function () {

            const postBody = {
                "ClientName": dataToPost.name,
				"ClientDescription": dataToPost.description
            };

            const apiUrl = '{0}Client?db={1}'.format(config.data.authentication.api, config.data.authentication.apiSource);

            // Here we pass a date time stamp to ensure the latest config is always loaded
            axios
                .post(apiUrl, postBody)
                .then(function (response) {

                    dispatch(appSetAlert(null, '{0} was successfully added.'.format(dataToPost.name), 'success', 10000));

                    // Refresh Dependant Data Sources
					dispatch(dataReloadDependants( ["vwClients"] ));

                    // Close the dialog and signal a reload
                    handleClose();

                })
                .catch(function (error) {
                    console.log('**ERROR**', error);

                    dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));

                    // TODO: Reset state of form
                    // TODO: Report issue on form
                });

        }, JSON.parse(axiosDelay()));
    };

	return (
        <Root className={classes.root}>
			<div className={classes.card}>
				{showProgress && dataSource.fetching &&
					<LinearProgress className={classes.linearProgress}/>
				}
				{showProgress && !dataSource.fetching &&
					<div className={classes.linearProgress}></div>
				}
				<form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
					<FormControl variant="standard" className={classes.margin}>
						<InputLabel htmlFor="input-with-icon-adornment">Client Name</InputLabel>
						<Input
							{...register("name")}
							name="name"
							id="name"
							startAdornment={
								<InputAdornment position="start">
									<StarIcon />
								</InputAdornment>
							}
							inputProps={{
								"aria-label": "Client Name"
							}}
						/>
						{errors.name && <p style={{color: 'red'}}>{errors?.name?.message}</p>}
					</FormControl>
					<FormControl variant="standard" className={classes.margin}>
						<InputLabel htmlFor="input-with-icon-adornment">Client Description</InputLabel>
						<Input
							{...register("description")}
							name="description"
							id="description"
							startAdornment={
								<InputAdornment position="start">
									<TextFieldsIcon />
								</InputAdornment>
							}
							inputProps={{
								"aria-label": "Client Description"
							}}
						/>
						{errors.description && <p style={{color: 'red'}}>{errors?.description?.message}</p>}
					</FormControl>
					<div className={classes.formEmbeddedActions}>
						<Button color={isThemeLight ? "primary" : "secondary"} onClick={() =>{ handleClose() }} disabled={formState.isSubmitting}>
							Cancel
						</Button>
						<Button color={isThemeLight ? "primary" : "secondary"} type="submit" disabled={formState.isSubmitting}>
							Submit
						</Button>
					</div>
				</form>
			</div>
		</Root>
    );
}